import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { HAS_DISMISSED_SHIPPING_LIMIT_BANNER, useUserSpecificLocalStorage } from '../../../hooks/use-local-storage';
import Global from '../../../store/global-selectors';
import { hasZeroShipmentLimit } from './has-zero-shipment-limit';
import { usePlanInfoDerivedSubscriptionInfo } from '../../../pages/settings/payment-and-subscription/hooks/use-derive-subscription-info';

export const PercentageValue = {
  EIGHTY: 80,
  NINETY_FIVE: 95,
  ONE_HUNDRED: 100,
};

export const useShippingPlanInfo = () => {
  const [hasDismissedBanner] = useUserSpecificLocalStorage(HAS_DISMISSED_SHIPPING_LIMIT_BANNER);
  const { isCTPPlan } = usePlanInfoDerivedSubscriptionInfo();
  const shipmentUsage = useSelector(Global.selectSellerShipmentUsage);

  const { isUnlimitedShipments: isUnlimited = false, shipmentLimit: limit, totalUsage: current } = shipmentUsage;

  const hasZeroLimit = useMemo(() => hasZeroShipmentLimit({ isUnlimited, limit }), [isUnlimited, limit]);
  const percentage = useMemo(() => {
    if (isUnlimited) return 0;
    if (limit === 0) return 100;
    return Math.round(((current ?? 0) / limit) * 100);
  }, [isUnlimited, limit, current]);

  const showBanner = useMemo(() => {
    if (percentage >= PercentageValue.EIGHTY && percentage < PercentageValue.NINETY_FIVE && !hasDismissedBanner) return true;
    if (hasZeroLimit) return true;
    // there are cases where CTP plans were shown shipment limit warnings while exceeding 100% usage, granted to them for business reasons (see 6664a04)
    return percentage >= PercentageValue.NINETY_FIVE && !(isCTPPlan && percentage > PercentageValue.ONE_HUNDRED);
  }, [percentage, hasDismissedBanner, hasZeroLimit, isCTPPlan]);

  return {
    showBanner,
    limit,
    current,
    percentage,
    isUnlimited,
    hasZeroLimit,
  };
};
